import {
    BOLD_ITALIC_STAR,
    BOLD_ITALIC_UNDERSCORE,
    BOLD_STAR,
    BOLD_UNDERSCORE,
    ITALIC_STAR,
    ITALIC_UNDERSCORE,
    LINK,
    ORDERED_LIST,
    STRIKETHROUGH,
    UNORDERED_LIST,
} from '@lexical/markdown';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import type {
    EditorThemeClasses,
    KlassConstructor,
    LexicalEditor,
    LexicalNode,
    SerializedEditorState,
} from 'lexical';
import type { ReactElement, ReactNode, Ref } from 'react';
import { cn } from '../util/classes.ts';
import { ValueSyncPlugin } from './value-sync-plugin.tsx';

export type RichTextValue = SerializedEditorState;

export type RichTextInputVariant = 'default' | 'inline';

const DEFAULT_TRANSFORMERS = [
    UNORDERED_LIST,
    ORDERED_LIST,
    BOLD_ITALIC_STAR,
    BOLD_ITALIC_UNDERSCORE,
    BOLD_STAR,
    BOLD_UNDERSCORE,
    ITALIC_STAR,
    ITALIC_UNDERSCORE,
    STRIKETHROUGH,
    LINK,
];

const URL_MATCHER =
    /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

const DEFAULT_LINK_MATCHERS = [
    (text: string) => {
        const match = URL_MATCHER.exec(text);
        if (match === null) {
            return null;
        }
        const fullMatch = match[0];
        return {
            index: match.index,
            length: fullMatch.length,
            text: fullMatch,
            url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
            attributes: { rel: 'noreferrer', target: '_blank' },
        };
    },
];

export type RichTextInputProps = {
    value: RichTextValue | undefined;
    onChange?: (value: RichTextValue) => void;
    placeholder?: string;

    // Lexical config supplied by the consumer so this component stays design-system-agnostic.
    theme: EditorThemeClasses;
    nodes: KlassConstructor<typeof LexicalNode>[];
    namespace?: string;
    onError?: (error: Error) => void;
    editorRef?: Ref<LexicalEditor>;

    variant?: RichTextInputVariant;
    error?: boolean;
    className?: string;
    contentClassName?: string;

    /**
     * Extra plugins (toolbar, autofocus, etc.) rendered inside the LexicalComposer.
     * The wrapper carries a `group` class so children can key visibility off
     * `group-focus-within:*` variants.
     */
    children?: ReactNode;
};

export function RichTextInput({
    value,
    onChange,
    placeholder,
    theme,
    nodes,
    namespace = 'RichTextInput',
    onError = () => {},
    editorRef,
    variant = 'default',
    error = false,
    className,
    contentClassName,
    children,
}: RichTextInputProps): ReactElement {
    const isInline = variant === 'inline';

    const initialConfig = {
        namespace,
        nodes,
        editorState: value ? JSON.stringify(value) : undefined,
        theme,
        onError,
    };

    return (
        <div
            className={cn(
                'group w-full px-default bg-container relative border',
                isInline
                    ? 'py-tight border-transparent border-b-line-weak rounded-none hover:border-b-line-strong focus-within:border-brand focus-within:rounded-tight'
                    : 'py-cozy rounded-default border-line-weak focus-within:border-brand',
                error && 'border-danger focus-within:border-danger',
                className,
            )}
        >
            <LexicalComposer initialConfig={initialConfig}>
                <RichTextPlugin
                    contentEditable={
                        <ContentEditable
                            className={cn(
                                'relative outline-hidden text-ui-lg sm:text-ui-base text-content overflow-auto',
                                isInline ? 'min-h-5 group-focus-within:min-h-16' : 'min-h-16',
                                contentClassName,
                            )}
                            aria-placeholder={placeholder ?? ''}
                            placeholder={
                                <span
                                    className={cn(
                                        'absolute left-default pointer-events-none text-ui-base text-content-weaker',
                                        isInline ? 'top-tight' : 'top-cozy',
                                    )}
                                >
                                    {placeholder}
                                </span>
                            }
                            style={{ userSelect: 'text' }}
                        />
                    }
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <ListPlugin />
                <LinkPlugin />
                <TablePlugin />
                <HistoryPlugin />
                <MarkdownShortcutPlugin transformers={DEFAULT_TRANSFORMERS} />
                <AutoLinkPlugin matchers={DEFAULT_LINK_MATCHERS} />
                {onChange && (
                    <OnChangePlugin
                        ignoreSelectionChange
                        onChange={(editor) => {
                            onChange(editor.toJSON());
                        }}
                    />
                )}
                {editorRef && <EditorRefPlugin editorRef={editorRef} />}
                <ValueSyncPlugin value={value} />
                {children}
            </LexicalComposer>
        </div>
    );
}
